import React from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

export interface ModalLayoutProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode | string
  description?: React.ReactNode | string
  children: React.ReactNode

  // Footer props
  cancelText?: string
  onCancel?: () => void
  confirmText?: string
  onConfirm?: () => Promise<void> | void
  isConfirmDisabled?: boolean
  confirmVariant?: 'primary' | 'danger'
  isLoading?: boolean

  // Optional customizer for icons (ConfirmModal)
  icon?: React.ReactNode

  // Optional form submit handling
  onSubmit?: (e: React.FormEvent) => void
}

export function ModalLayout({
  open,
  onOpenChange,
  title,
  description,
  children,
  cancelText = 'ยกเลิก',
  onCancel,
  confirmText = 'บันทึก',
  onConfirm,
  isConfirmDisabled = false,
  confirmVariant = 'primary',
  isLoading = false,
  icon,
  onSubmit,
}: ModalLayoutProps) {
  const handleCancel = () => {
    onOpenChange(false)
    if (onCancel) onCancel()
  }

  const handleConfirm = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (onConfirm) {
      await onConfirm()
    }
  }

  const contentBody = (
    <>
      {/* Top Half: Header & Content Body */}
      <div className="space-y-6 p-8 pb-6">
        <DialogHeader
          className={
            icon ? 'flex flex-col items-center text-center' : 'text-left'
          }
        >
          {icon && <div className="mb-2">{icon}</div>}
          <DialogTitle className="text-2xl leading-tight font-bold tracking-tight text-gray-900">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="mt-1 text-sm leading-normal font-bold text-gray-500">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Content Area */}
        <div className="space-y-4">{children}</div>
      </div>

      {/* Bottom Half: Premium Pink Footer Bar */}
      <DialogFooter className="flex flex-row items-center justify-between gap-4 rounded-b-[28px] border-t border-rose-100/30 bg-rose-50/60 px-8 py-3 sm:flex-row sm:justify-between">
        <Button
          type="button"
          variant="ghost"
          className="h-auto border-0 bg-transparent px-0 font-bold text-gray-500 shadow-none hover:bg-transparent hover:text-gray-900"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          type={onSubmit ? 'submit' : 'button'}
          disabled={isConfirmDisabled || isLoading}
          onClick={onSubmit ? undefined : () => handleConfirm()}
          className={cn(
            'h-11 rounded-full px-8 font-bold text-white shadow-md transition-all active:scale-95',
            confirmVariant === 'danger'
              ? 'bg-rose-500 shadow-rose-500/10 hover:bg-rose-600'
              : 'bg-rose-500 shadow-rose-500/10 hover:bg-rose-600',
          )}
        >
          {confirmText}
        </Button>
      </DialogFooter>
    </>
  )

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="gap-0 overflow-hidden rounded-[28px] border-0 bg-white p-0 shadow-2xl sm:max-w-112.5">
        {onSubmit ? (
          <form
            onSubmit={handleConfirm}
            className="contents"
          >
            {contentBody}
          </form>
        ) : (
          contentBody
        )}
      </DialogContent>
    </Dialog>
  )
}
